<template>
    <div 
        id="ecm" 
        v-show="isMounted" 
        @keyup="onKeyup($event)"
        :class="[theme, {fullscreen: fullscreen, boxed: boxed}]"
        :style="inlineStyles()">

        <div v-if="showLoader" class="ecm-loader"></div>

        <Map v-if="config" />

        <MapControls v-if="config" />

        <button
            id="ecmSettingsBtn"
            @click="toggleSettings">
            <SvgIcon 
                v-show="!settingsOpen"
                icon="icon-settings"
                title="Menu">
            </SvgIcon>
            <SvgIcon 
                v-show="settingsOpen"
                icon="icon-close"
                title="Close settings">
            </SvgIcon>
            <span class="sr-only">Toggle menu</span>
        </button>

        <SettingsPanel
            v-if="config"
        ></SettingsPanel>

        <Status />

        <Article
            v-if="article.title"
            v-bind="article"
        ></Article>

        <Modal
            v-if="$store.state.modal"
            :title="$store.state.modal.title"
            :body="$store.state.modal.body"
            :btnLabel="$store.state.modal.btnLabel"
        />

    </div>
</template>

<script>
import { eventBus } from './main.js';
import { store } from './store/store';
import { i18n } from './main.js';
import axios from 'axios';
//import haversine from 'haversine-distance';
import Map from './components/Map.vue';
import SvgIcon from './components/SvgIcon.vue';
import MapControls from './components/MapControls.vue';
import Modal from './components/Modal.vue';
import SettingsPanel from './components/SettingsPanel.vue';
import Status from './components/Status.vue';
import Article from './components/Article.vue';

export default {
    name: 'App',
    data: function () {
        return {
            loading: false,
            fatalErrors: [],
            isMounted: false,
            dataset: {},
            article: {},
            title: null,
            visitSessionHash: null,
            visitSessionInterval: null,
            visitSessionViewId: null
        }
    },
    store: store,
    components: {
        Map,
        SvgIcon,
        MapControls,
        Modal,
        SettingsPanel,
        Status,
        Article
    },
    computed: {
        lan() {
            return store.state.lan;
        },
        config() {
            return store.state.config;
        },
        showLoader() {
            if (this.fatalErrors.length > 0) {
                return false;
            }
            return ! this.config || this.loading;
        },
        markers() {
            return store.state.markers;
        },
        routes() {
            return store.state.routes;
        },
        showMarkers() {
            return this.$store.state.showMarkers;
        },
        showRoutes() {
            return this.$store.state.showRoutes;
        },
        routesDisabled() {
            return this.$store.state.routesDisabled;
        },
        fullscreen() {
            return store.state.fullscreen;
        }, 
        boxed() {
            if (this.dataset && this.dataset.boxed) {
                // Boxed is set with data-boxed attribute
                return true;
            } else if (this.config && this.config.boxed) {
                // Boxed is set in map config
                return true;
            }
            return false;
        },
        settingsOpen() {
            return store.state.settingsOpen;
        },
        theme() {
            if (store.state.config && store.state.config.theme) {
                return store.state.config.theme;
            }
            return null;
        },
        themeColor() {
            if (store.state.config && store.state.config.themeColor) {
                return store.state.config.themeColor;
            }
            return null;
        },
        themeTextColor() {
            if (store.state.config && store.state.config.themeColor) {
                return this.$setThemeTextColor(store.state.config.themeColor);
            }
            return null;
        },
        themeHoverColor() {
            if (store.state.config && store.state.config.themeColor) {
                return this.$setThemeHoverColor(store.state.config.themeColor);
            }
            return null;
        },
        locationsDBRequestParams() {
            let params = {
                lan: this.lan,
                userId: this.config.userId,
                workspaceId: this.config.workspaceId,
                sourceIds: this.config.sourceIds.join(','),
                categoryIds: this.$store.state.checkedCategories.join(',')
            };

            if (this.config.anyLan) {
                params.anyLan = 'true';
            }
            
            return params;
        }
    },
    created() {
        // Ecm element data attributes have to be get before mounting!
        // this.$el is not accessible yet so we have to use traditional 
        // methods for referencing div#ecm 
        let ecmEl = document.getElementById('ecm');
        
        // Loop through div#ecm dataset and add them to this.dataset
        for (let attr in ecmEl.dataset) {
            this.dataset[attr] = ecmEl.dataset[attr];
        }
        
        // Get language from html lang attribute
        if (document.documentElement.lang) {
            // Get only the first two characters since it might be like "en-GB"
            store.commit('setLan', document.documentElement.lang.slice(0, 2));
        }
        
        // Override html lang attribute with data-lan
        if (this.dataset.lan) {
            store.commit('setLan', this.dataset.lan);
        }

        // Get page title
        this.title = document.querySelector('title').textContent;

        // Event bus toggle fullscreen handler
        eventBus.$on('toggleFullscreen', this.toggleFullscreen);

        // Event bus load markers handler
        eventBus.$on('loadMarkers', this.loadMarkers);

        // Event bus load routes handler
        eventBus.$on('loadRoutes', this.loadRoutes);

        // Event bus load externals handler
        eventBus.$on('loadExternals', this.loadExternals);

        // Event bus load article handler
        eventBus.$on('loadArticle', this.loadArticle);

        // Event bus close article handler
        eventBus.$on('closeArticle', this.closeArticle);

        // Event bus infoWindowOpen handler
        eventBus.$on('infoWindowOpen', (viewData) => {
            viewData.visitSessionHash = this.visitSessionHash;
            this.postItemView(viewData);
        });

        // Event bus articleOpen handler
        eventBus.$on('articleOpen', (viewData) => {
            viewData.visitSessionHash = this.visitSessionHash;
            this.postItemView(viewData);
        });

        /*window.onhashchange = (e) => {
            console.log('hashchange');
            console.log(e);
        };*/

        // Handle history changes
        //window.onpopstate = (e) => {
            //console.log('popstate');
            //console.log(JSON.stringify(e.state));
            
            /*if (e.state !== null && e.state.view == 'article') {
                this.loadArticle({articleApiUrl: e.state.articleApiUrl}, true);
            } else if (window.location.hash !== null && window.location.hash !== '') {
                this.checkHash();
            }*/
            
            /*if (e.state == null || ! e.state.view) {
                // Back to basic view
                console.log('state is null');
                if (this.article) {
                    console.log('should close article');
                    this.closeArticle();
                }
            } else if (e.state != null && e.state.view == 'article') {
                this.loadArticle({articleApiUrl: e.state.articleApiUrl}, true);
            }
            this.setTitle(e.state);*/
        //};

        // window.emitter events from outside KarttaMap (for example Dashboard)
        if (window.emitter) {

            window.emitter.on('setMapLan', (lan) => {
                let lanChanged = this.lan != lan;
                store.commit('setLan', lan);
                // Show welcome message always on language change
                // setMapLan may be emitted even if the language doesn't change
                if (lanChanged) {
                    eventBus.$emit('closeModal');
                    this.showWelcomeMessage();
                }
            });

            window.emitter.on('setMapConfig', (config) => {
                let showWelcomeMessage = this.welcomeMessageChanged(config.welcomeMessage);
                this.$store.commit('setConfig', config);
                i18n.locale = this.lan;
                i18n.mergeLocaleMessage(this.lan, this.config.texts[this.lan]);
                // Invalidate map size in case width or height has changed
                eventBus.$emit('mapInvalidateSize');
                // Reload categories so new categories will get their names and icons
                eventBus.$emit('loadCategories');
                // Emit events to Map to reload all stuff!
                // This has to be done with a slight delay or externals won't update
                window.setTimeout(() => {
                    eventBus.$emit('reloadAllMarkersAndRoutes');
                }, 500);
                //eventBus.$emit('reloadAllMarkersAndRoutes');
                eventBus.$emit('setBaseLayer', null);
                eventBus.$emit('removeStatus', 'Loading config');
                // Close/open settings panel if needed
                if (this.config.settingsPanelClosed) {
                    this.$store.commit('setSettingsOpen', false);
                } else {
                    this.$store.commit('setSettingsOpen', true);
                }
                // Show welcome message if it has changed
                if (showWelcomeMessage) {
                    this.showWelcomeMessage();
                }
            });

            // Set add params event
            window.emitter.on('setAddParams', (data) => {
                let dataAttrName = 'addParams' + data.name.charAt(0).toUpperCase() + data.name.slice(1);
                this.dataset[dataAttrName] = data.params;
                eventBus.$emit('externalFilterChange', data.name);
            });

            window.emitter.on('removeMapListeners', this.removeListeners);
        }
    },
    mounted() {
        this.isMounted = true;

        // Check for preview mode first
        if (this.dataset.preview) {
            eventBus.$emit('addStatus', 'Loading config');
            if (window.emitter) {
                window.emitter.emit('previewMapReady');
            }
        } else if (this.dataset.mapKey) {
            // No preview mode set, load map config
            this.loadConfig();
        }
    },
    beforeDestroy() {
        this.removeListeners();
    },
    methods: {
        onKeyup(e) {
            if (e.key === "Escape") {
                this.$store.commit('setFullscreen', false);
                eventBus.$emit('refreshMapSize');
            }
        },
        loadConfig() {
            eventBus.$emit('addStatus', 'Loading config');
            axios.get(process.env.VUE_APP_KARTTAMAP_API_PATH + 'map-config/' + this.dataset.mapKey)
            .then((response) => {
                if (! response.data.errors) {
                    // Override config with data attributes if set
                    response.data.config = this.setConfigOverrides(response.data.config);
                    this.$store.commit('setConfig', response.data.config);
                    i18n.locale = this.lan;
                    i18n.setLocaleMessage(this.lan, this.config.texts[this.lan]);
                    this.setTop();
                    // Close settings panel if needed
                    if (response.data.config.settingsPanelClosed) {
                        this.$store.commit('setSettingsOpen', false);
                    }
                    // Open demo mode modal if needed
                    if (response.data.demoMode) {
                        store.commit('setModal', {
                            title: 'KarttaMap Free',
                            body: this.$t('demoModeText'),
                            btnLabel: this.$t('close')
                        });
                    }
                    else if (this.config.welcomeMessage && this.config.welcomeMessage[this.lan]) {
                        // Set welcome message
                        this.showWelcomeMessage();
                    }
                    if (! window.emitter) {
                        // Not in Dashboard, add map view to db
                        this.postMapView();
                    }
                    eventBus.$emit('removeStatus', 'Loading config');
                } else {
                    // Output errors in modal
                    let errorsText = '';
                    this.fatalErrors = response.data.errors;
                    for (let i in this.fatalErrors) {
                        console.error(this.fatalErrors[i]);
                        errorsText += this.fatalErrors[i] + '<br /><br />';
                    }
                    store.commit('setModal', {
                        title: 'ERROR',
                        body: errorsText
                    });
                }
            })
            .catch(function(error) {
                console.error('Error loading config: ' + error);
            });
        },
        loadMarkers(bounds) {
            if (this.config.markersUrl && this.showMarkers) {
                eventBus.$emit('addStatus', 'Loading markers');

                let url = this.replaceUrlParams(this.config.markersUrl, bounds);
    
                axios.get(url, {
                    params: this.locationsDBRequestParams
                })
                .then((response) => {
                    this.$store.commit('setMarkers', response.data);
                    eventBus.$emit('removeStatus', 'Loading markers');
                    this.checkHash();
                })
                .catch(function(error) {
                    console.error('Error loading markers: ' + error);
                });
            }
        },
        loadRoutes(bounds) {
            if (this.config.routesUrl && this.showRoutes && ! this.routesDisabled) {
                eventBus.$emit('addStatus', 'Loading routes');

                let url = this.replaceUrlParams(this.config.routesUrl, bounds);

                axios.get(url, {
                    params: this.locationsDBRequestParams
                })
                .then((response) => {
                    // Check that all routes waypoints are arrays
                    for (let i = 0; i < response.data.length; i++) {
                        if (! Array.isArray(response.data[i].waypoints)) {
                            // Not an array, delete route
                            response.data.splice(i, 1);
                        }
                    }
                    this.$store.commit('setRoutes', response.data);
                    eventBus.$emit('removeStatus', 'Loading routes');
                    this.checkHash();
                })
                .catch(function(error) {
                    console.error('Error loading routes: ' + error);
                });
            }
        },
        loadExternals(sourceId, bounds) {
            let source = this.getExternalSourceById(sourceId);
            
            if (! source) {
                // Source was not found for some reason, abort
                this.$store.commit('removeExternalMarkers', sourceId);
                return;
            }

            eventBus.$emit('addStatus', 'Loading external sources');

            // Put map bounds to url parameters
            let url = this.replaceUrlParams(source.url, bounds);
            
            // Additional parameters from data attribute
            let dataAttrName = 'addParams' + sourceId.charAt(0).toUpperCase() + sourceId.slice(1);
            if (this.dataset[dataAttrName]) {
                //console.log(this.dataset[dataAttrName]);
                if (url.search('\\?') == -1) {
                    url += '?';
                }
                url += this.dataset[dataAttrName];
            }
            
            axios.get(url)
            .then((response) => {
                // TO DO: support for data.places and data.routes format

                let extMarkersArr = [];
                let extRoutesArr = [];
                
                if (response.data.length > 0) {
                    if (response.data[0].latitude && response.data[0].longitude) {
                        // Latitude & longitude set, these are places
                        extMarkersArr = response.data;
                    } else if (response.data[0].geometry) {
                        // Geometry set, these are Retkipaikka places/routes
                        extMarkersArr = response.data;
                    } else if (response.data[0].waypoints) {
                        // Waypoints set, these are routes
                        extRoutesArr = response.data;
                    }

                    // Add sourceId and articleApiUrl to markers data
                    extMarkersArr.forEach((item) => {
                        item.name = source.id;
                        if (source.articleApiUrl) {
                            item.articleApiUrl = source.articleApiUrl;
                            item.articleApiUrl = this.replaceUrlParams(item.articleApiUrl, bounds);
                        } else {
                            // No articleApiUrl, set to external
                            item.external = true;
                        }
                    });

                    // Add sourceId and articleApiUrl to routes data
                    extRoutesArr.forEach((item) => {
                        item.name = source.id;
                        if (source.articleApiUrl) {
                            item.articleApiUrl = source.articleApiUrl;
                            item.articleApiUrl = this.replaceUrlParams(item.articleApiUrl, bounds);
                        } else {
                            // No articleApiUrl, set to external
                            item.external = true;
                        }
                    });

                    // Convert data with callback function if defined
                    if (source.callback) {
                        if (this.isFunction(source.callback)) {
                            // Callback is a function
                            if (extMarkersArr.length) {
                                extMarkersArr = source.callback(extMarkersArr, source);
                            }
                            if (extRoutesArr.length) {
                                extRoutesArr = source.callback(extRoutesArr, source);
                            }
                        } else {
                            // Callback is a method in App.vue
                            if (extMarkersArr.length) {
                                extMarkersArr = this[source.callback](extMarkersArr, source);
                            }
                            if (extRoutesArr.length) {
                                extRoutesArr = this[source.callback](extRoutesArr, source);
                            }
                        }
                    }
                }

                this.$store.commit('setExternalMarkers', {
                    name: source.id,
                    markers: extMarkersArr
                });

                this.$store.commit('setExternalRoutes', {
                    name: source.id,
                    routes: extRoutesArr
                });

                eventBus.$emit('pruneClusterReDraw');
                eventBus.$emit('removeStatus', 'Loading external sources');
                extMarkersArr = null;
                extRoutesArr = null;
                this.checkHash();
            })
            .catch(function(error) {
                console.error('Error loading external sources: ' + error);
            });
        },
        /**
         * @param   {object}    data
         * @param   {boolean}   callFromHistory
         */
        loadArticle(data, callFromHistory) {
            this.loading = true;
            this.visitSessionViewId = null;

            // Retkipaikka data loaded from Retkipaikka API is handled differently
            if (data.sourceId && data.sourceId.search('retkipaikka') !== -1 && data.sourceId.search('retkipaikkaArticles') === -1 && data.sourceId.search('retkipaikkaMarkers') === -1) {
                this.openRetkipaikkaArticle(data);
                return;
            }

            let params = {
                lan: this.lan
            };

            if (this.config.anyLan) {
                params.anyLan = 'true';
            }
            
            axios.get(data.articleApiUrl, {
                    params: params
            })
            .then((response) => {
                //console.log(response);
                this.article = response.data;
                this.article.mapKey = this.dataset.mapKey;
                
                let historyState = {
                    view: 'article',
                    id: this.article.id,
                    slug: this.article.slug,
                    title: this.article.title,
                    articleApiUrl: data.articleApiUrl
                };

                if (! this.article.waypoints) {
                    // No waypoints, it's a place
                    historyState.type = 'place';
                } else {
                    // Has waypoints, it's a route
                    historyState.type = 'route';
                }

                if (data.sourceId) {
                    historyState.sourceId = data.sourceId;
                    this.article.sourceId = data.sourceId;
                }

                // Don't set history only if the call came from history popstate!
                /*if (! callFromHistory) {
                    this.setHistory(historyState);
                }*/

                // Set url hash if routing not enabled and call not from history
                if (! this.config.routing && ! callFromHistory) {
                    this.setUrlHash(historyState);
                }

                // Set viewData object
                let viewData = {
                    visitSessionHash: this.visitSessionHash,
                    viewType: 'ARTICLE_OPEN'
                };

                if (data.articleApiUrl.search(process.env.VUE_APP_LOCATIONS_API_PATH) !== -1) {
                    // Set placeId or routeId for Locations DB articles
                    if (data.articleApiUrl.search('/places/') !== -1) {
                        viewData.placeId = response.data.id;
                    } else if (data.articleApiUrl.search('/routes/') !== -1) {
                        viewData.routeId = response.data.id;
                    }
                } else {
                    // Set external stuff
                    viewData.externalId = response.data.id;
                    viewData.externalApiUrl = data.articleApiUrl;
                    if (response.data.title) {
                        viewData.externalTitle = response.data.title;
                    }
                    if (response.data.url) {
                        viewData.externalUrl = response.data.url;
                    }
                }

                // Post view
                this.postItemView(viewData);

                // Convert html entities to text in title
                this.article.title = this.htmlEntities2Text(this.article.title);
                
                // Explories article has text, WP article has content
                if (response.data.text) {
                    this.article.content = response.data.text;
                }

                // Add publication logo and theme color if exists
                if (typeof response.data.publication !== 'undefined') {
                    if (response.data.publication.image) {
                        this.article.logo = response.data.publication.image;
                    }

                    if (response.data.publication.themeColor) {
                        this.article.themeColor = response.data.publication.themeColor;
                    }
                }

                this.loading = false;
            })
            .catch(function(error) {
                console.error('Error loading article: ' + error);
            });
        },
        openRetkipaikkaArticle(data, callFromHistory) {
            this.loading = true;
            this.visitSessionViewId = null;
            this.article = this.getExternalMarkerById(data.sourceId, data.id);
            
            let historyState = {
                view: 'article',
                id: this.article.id,
                slug: this.article.slug,
                title: this.article.title
            };

            if (! this.article.waypoints) {
                // No waypoints, it's a place
                historyState.type = 'place';
            } else {
                // Has waypoints, it's a route
                historyState.type = 'route';
            }

            if (data.sourceId) {
                historyState.sourceId = data.sourceId;
            }

            // Set url hash if routing not enabled and call not from history
            if (! this.config.routing && ! callFromHistory) {
                this.setUrlHash(historyState);
            }

            // Set viewData object
            let viewData = {
                visitSessionHash: this.visitSessionHash,
                viewType: 'ARTICLE_OPEN'
            };

            viewData.externalId = data.id;
            if (data.title) {
                viewData.externalTitle = data.title;
            }
            if (data.url) {
                viewData.externalUrl = data.url;
            }

            // Post view
            this.postItemView(viewData);

            // Convert html entities to text in title
            this.article.title = this.htmlEntities2Text(this.article.title);

            this.loading = false;
        },
        closeArticle() {
            // If visitSessionViewId is set update view data when article is closed
            if (this.visitSessionViewId) {
                this.postItemView({
                    id: this.visitSessionViewId
                });
            }
            this.article = {};
            // Remove hash if routing not enabled
            if (! this.config.routing) {
                this.setUrlHash(null);
            }
        },
        toggleSettings() {
            this.$store.commit('toggleSettingsOpen');
        },
        toggleFullscreen() {
            this.$store.commit('setFullscreen', ! this.fullscreen);
            eventBus.$emit('refreshMapSize');
        },
        /**
         * Posts map view to API.
         */
        postMapView() {
            if (window.emitter) {
                // We're in Dashboard, don't post
                return;
            }

            let formData = new FormData();
            // Always set mapKey and lan
            formData.append('mapKey', this.dataset.mapKey);
            formData.append('lan', this.lan);
            // Set hash or userAgent
            if (this.visitSessionHash) {
                formData.append('hash', this.visitSessionHash);
            } else {
                formData.append('userAgent', window.navigator.userAgent);
            }
            
            axios.post(process.env.VUE_APP_KARTTAMAP_API_PATH + 'map-view', formData)
            .then((response) => {
                //console.log(response);
                if (response.data.visitSessionHash) {
                    this.visitSessionHash = response.data.visitSessionHash;
                    // Set interval and beforeunload
                    if (! this.visitSessionInterval) {
                        this.visitSessionInterval = window.setInterval(this.postMapView, 5000);
                        // Post map view once more before unload
                        window.onbeforeunload = () => {
                            window.clearInterval(this.visitSessionInterval);
                            this.postMapView();
                            // If visitSessionViewId is set update view data
                            if (this.visitSessionViewId) {
                                this.postItemView({
                                    id: this.visitSessionViewId
                                });
                            }
                        }
                    }
                }
            })
            .catch((error) => {
                console.error(error);
            });
        },
        /**
         * Posts item view to API.
         */
        postItemView(viewData) {
            if (window.emitter) {
                // We're in Dashboard or visitSessionHash not set, don't post
                return;
            }

            let formData = new FormData();
            
            if (viewData.id) {
                formData.append('id', viewData.id);
            } else {
                let viewDataPostProps = [
                    'visitSessionHash',
                    'viewType',
                    'placeId',
                    'routeId',
                    'externalId',
                    'externalTitle',
                    'externalUrl',
                    'externalApiUrl',
                    'externalSourceName'
                ];

                // Loop through viewDataPostProps and add to formData
                for (let i in viewDataPostProps) {
                    if (viewData[viewDataPostProps[i]]) {
                        formData.append(viewDataPostProps[i], viewData[viewDataPostProps[i]]);
                    }
                }
            }
            
            axios.post(process.env.VUE_APP_KARTTAMAP_API_PATH + 'item-view', formData)
            .then((response) => {
                //console.log(response);
                if (response.data.viewId) {
                    this.visitSessionViewId = response.data.viewId;
                }
            })
            .catch((error) => {
                console.error(error);
            });
        },
        removeListeners() {
            eventBus.$off('toggleFullscreen', this.toggleFullscreen);
            eventBus.$off('loadMarkers', this.loadMarkers);
            eventBus.$off('loadRoutes', this.loadRoutes);
            eventBus.$off('loadExternals', this.loadExternals);
            eventBus.$off('loadArticle', this.loadArticle);
            eventBus.$off('closeArticle', this.closeArticle);
            if (window.emitter) {
                window.emitter.all.delete('setMapConfig');
                window.emitter.all.delete('removeMapListeners');
            }
        },
        getOffsetTopPx() {
            if (! this.config || ! this.config.headerSelector) {
                return;
            }

            let ecmTop = 0;
            let header = document.querySelector(this.config.headerSelector);
            let wpAdminBar = document.getElementById('wpadminbar');

            if (header) {
                ecmTop = header.offsetHeight;
            }
            // Add wpAdminbar height to top value if needed
            if (wpAdminBar) {
                ecmTop += wpAdminBar.offsetHeight;
            }
            return ecmTop + 'px';
        },
        setTop() {
            if (! this.config.setTopFromHeader || this.config.boxed) {
                return;
            }

            let ecm = document.getElementById('ecm');
            
            ecm.style.top = this.getOffsetTopPx();
            
            // Update top value on window resize
            window.addEventListener('resize', () => {
                ecm.style.top = this.getOffsetTopPx();
            });
        },
        showWelcomeMessage() {
            if (this.config && this.config.welcomeMessage && this.config.welcomeMessage[this.lan]) {
                store.commit('setModal', {
                    title: this.config.welcomeMessage[this.lan].title,
                    body: this.config.welcomeMessage[this.lan].body,
                    btnLabel: this.config.welcomeMessage[this.lan].btnLabel
                });
            }
        },
        /**
         * Compares new and old welcomeMessage.
         * @param   {object}    newWelcomeMessage
         * @return  {boolean}
         */
        welcomeMessageChanged(newWelcomeMessage) {
            if (! this.config && newWelcomeMessage) {
                return true;
            } else if (this.config && this.config.welcomeMessage && newWelcomeMessage) {
                return JSON.stringify(this.config.welcomeMessage) !== JSON.stringify(newWelcomeMessage);
            }
            return false;
        },
        /**
         * @param   {string}    paramsStr
         * @return  {object}
         */
        mapUrlParams(paramsStr) {
            let urlParams = paramsStr.split('/');
            return {
                type: urlParams[0],
                id: urlParams[1],
                slug: urlParams[2]
            };
        },
        /**
         * Overrides config options from data attributes
         * for specific config options.
         * 
         * @param   {object}    config
         * @returns {object}
         */
        setConfigOverrides(config) {
            // Zoom
            if (this.dataset.zoom) {
                config.initZoom = parseInt(this.dataset.zoom);
            }
            // Lat & lng
            if (this.dataset.lat && this.dataset.lng) {
                config.initLocation = {
                    lat: this.dataset.lat,
                    lng: this.dataset.lng
                };
            }
            // fullscreenOnFocus
            if (this.dataset.fullscreenOnFocus == 'true') {
                config.fullscreenOnFocus = true;
            }
            // Always showFullscreenToggle if using boxed
            if (this.dataset.boxed) {
                config.showFullscreenToggle = true;
            }
            // fullscreenOnFocus
            if (this.dataset.anyLan == 'true') {
                config.anyLan = true;
            }
            return config;
        },
        getExternalSourceById(id) {
            let source;
            for (let sourceSet in this.config.externalSources) {
                let sources = this.config.externalSources[sourceSet].sources;
                let match = sources.find(item => item.id == id);
                if (match) {
                    source = match;
                    break;
                }
            }
            return source;
        },
        getExternalMarkerById(sourceId, id) {
            for (let i in store.state.externalMarkers[sourceId]) {
                let marker = store.state.externalMarkers[sourceId][i];
                if (marker.id == id) {
                    return marker;
                }
            }
            return null;
        },
        checkHash() {
            if (window.location.hash) {
                let urlParams = this.mapUrlParams(window.location.hash.replace('#', ''));
                let source;
                let extMarkerId;
                let sourceId;
                
                // Always close modal if hash is set
                store.commit('setModal', null);

                // If id is NaN it's external
                if (isNaN(urlParams.id)) {
                    // Check that we have external sources
                    if (! this.config.externalSources) {
                        console.error('No external sources');
                        return;
                    }
                    
                    let idParts = urlParams.id.split('-');
                    extMarkerId = idParts[idParts.length - 1];
                    sourceId = urlParams.id.replace('-' + extMarkerId, '');

                    // Retkipaikka data cannot be opened with a direct url at the moment
                    if (sourceId.search('retkipaikka') !== -1 && sourceId.search('retkipaikkaArticles') === -1 && sourceId.search('retkipaikkaMarkers') === -1) {
                        window.location.hash = '';
                        return;
                    }

                    // Get articleApiUrl from config.externalSources[sourceSet].sources
                    for (const prop in this.config.externalSources) {
                        let sourceSet = this.config.externalSources[prop];
                        source = sourceSet.sources.find(item => item.id == sourceId);

                        if (source) {
                            this.loadArticle({
                                articleApiUrl: source.articleApiUrl.replace('{id}', extMarkerId),
                                sourceId: sourceId
                            });
                            break;
                        }
                    }
                } else if (urlParams.type == 'place') {
                    this.loadArticle({
                        articleApiUrl: this.config.markersBaseUrl + '/' + urlParams.id
                    });
                } else if (urlParams.type == 'route') {
                    this.loadArticle({
                        articleApiUrl: this.config.routesBaseUrl + '/' + urlParams.id
                    });
                }
            }
        },
        setUrlHash(historyState) {
            let statePath = null;
            
            if (historyState !== null) {
                let statePathId = historyState.id;

                // Add external source id if needed
                if (historyState.sourceId) {
                    statePathId = historyState.sourceId + '-' + historyState.id;
                }

                statePath = historyState.type + '/' + statePathId;
                
                if (historyState.slug) {
                    statePath += '/' + historyState.slug
                }

                // Replace history state with hash url to prevent back/forward buttons to mess it up
                history.replaceState(undefined, undefined, '#' + statePath);
            } else {
                //window.location.hash = '';
                history.replaceState(undefined, undefined, '#');
            }

            this.setTitle(historyState);
        },
        /**
         * @param   {object}            historyState
         * @param   {string}            historyState.view
         * @param   {number|string}     historyState.id
         * @param   {string}            historyState.slug
         * @param   {string}            historyState.title
         * @param   {string}            historyState.articleApiUrl
         */
        /*setHistory(historyState) {
            if (! this.config.routing) {
                // No routing enabled, abort
                return;
            }

            this.setTitle(historyState);
            
            let statePath = null;
            
            if (historyState !== null) {
                let statePathId = historyState.id;

                // Add external source id if needed
                if (historyState.sourceId) {
                    statePathId = historyState.sourceId + '-' + historyState.id;
                }

                statePath = historyState.type + '/' + statePathId + '/' + historyState.slug;
            }

            if (historyState === null) {
                history.pushState(historyState, '', '');
                //history.back();
                return;
            }
            history.pushState(historyState, '', statePath);
        },*/
        /**
         * @param   {object}            historyState
         * @param   {string}            historyState.view
         * @param   {number|string}     historyState.id
         * @param   {string}            historyState.slug
         * @param   {string}            historyState.title
         * @param   {string}            historyState.articleApiUrl
         */
        setTitle(historyState) {
            let title = document.querySelector('title');
            let ogTitle = document.querySelector('meta[property="og:title"]');
            let ogUrl = document.querySelector('meta[property="og:url"]');

            if (historyState && historyState.title) {
                // Title set in historyState, change title textContent
                title.textContent =  historyState.title + this.config.titleSeparator + this.title;
            } else {
                // No title in historyState, use original title
                title.textContent = this.title;
            }

            // Set og title if exists
            if (ogTitle) {
                ogTitle.setAttribute('content', title.textContent);
            }

            // Set og url if exists
            if (ogUrl) {
                ogUrl.setAttribute('content', window.location.href);
            }
        },
        /**
         * @param   {array}     data
         * @param   {object}    source
         */
        convertLN2ECM(data, source) {

            let extMarkersArr = [];

            for (let i = 0; i < data.length; i++) {
                // Use thumbnail if images not defined
                if (! data[i].images) {
                    data[i].images = {
                        medium: data[i].thumbnail
                    };
                }

                // Defaults
                let url = 'https://explori.es/fi/Article/' + data[i].id + '/' + data[i].slug;
                let target = '_blank';
                let external = true;
                let articleApiUrl = null;

                // Set article api url if defined
                if (source.articleApiUrl) {
                    articleApiUrl = source.articleApiUrl;
                    articleApiUrl = articleApiUrl.replace('{id}', data[i].id);
                    articleApiUrl = articleApiUrl.replace('{slug}', data[i].slug);
                    articleApiUrl = articleApiUrl.replace('{lan}', this.lan);
                    target = '_self';
                    external = false;
                }

                // External can be overrided with external markers property external
                if (typeof source.external !== 'undefined') {
                    external = source.external;
                }
                
                // If data has original url use it
                if (data[i].url) {
                    url = data[i].url;
                }

                // Convert html entities to text in title
                data[i].title = this.htmlEntities2Text(data[i].title);

                let marker = {
                    id: data[i].id,
                    name: source.id,
                    title: data[i].title,
                    slug: data[i].slug,
                    image: data[i].images.medium,
                    logo: data[i].publication.image,
                    color: data[i].publication.themeColor,
                    latitude: parseFloat(data[i].latitude),
                    longitude: parseFloat(data[i].longitude),
                    url: url,
                    articleApiUrl: articleApiUrl,
                    hasText: true,
                    target: target,
                    icon: data[i].publication.marker,
                    external: external,
                    iconSize: source.iconSize
                };

                // Set article api url using history
                if (this.config.routing && source.slug && source.articleApiUrl) {
                    marker.articleApiUrl = source.articleApiUrl;
                    marker.articleApiUrl = marker.articleApiUrl.replace('{id}', data[i].id);
                }

                extMarkersArr.push(marker);
            }
            return extMarkersArr;
        },
        /**
         * @param   {array}     data
         * @param   {object}    source
         * @return  {array}
         */
        convertRetkipaikka2ECM(data, source) {

            let extMarkersArr = [];
            let extRoutesArr = [];

            for (let i = 0; i < data.length; i++) {
                
                let allExternalMarkers = store.state.externalMarkers;
                let existsAlready = false;

                // Check if marker exists already
                for (var propName in allExternalMarkers) {
                    if (allExternalMarkers[propName] && propName.search('retkipaikka') != -1 && propName != source.id) {
                        existsAlready = allExternalMarkers[propName].some(function(el) {
                            return el.id === data[i].id
                        });

                        if (existsAlready) {
                            //console.log('Duplicate ' + data[i].id + ': ' + data[i].title);
                            break;
                        }
                    }
                }

                // Exists already, skip this one
                if (existsAlready) {
                    continue;
                }

                let target = '_blank';
                let external = true;
                let icon = 'https://kartta.retkipaikka.fi/assets/marker.png';
                let markerLat;
                let markerLng;
                let route;
                let waypoints;

                // Retkipaikka route difficulties
                let badgeIds = {
                    esteeton: 0,
                    helppo: 1,
                    keskivaativa: 2,
                    vaativa: 3,
                    luokittelematon: 4
                };

                let routeColors = {
                    esteeton: "#3c3",
                    helppo: "#03c",
                    keskivaativa: "#c33",
                    vaativa: "#000",
                    luokittelematon: "#cc3"
                };
                
                if (data[i].marker) {
                    icon = 'https://kartta.retkipaikka.fi/assets/' + data[i].marker;
                }

                // Use filter icon if marker icon is the default
                if (data[i].marker == 'marker.png' && source.filtersIcon) {
                    icon = source.filtersIcon;
                }

                // Use thumbnail if images not defined
                if (! data[i].images) {
                    data[i].images = {
                        medium: data[i].thumbnail
                    };
                }

                // Link points to retkipaikka.fi, always internal
                if (data[i].link != null && data[i].link.search('retkipaikka.fi')) {
                    external = false;
                }

                // Can be overrided with external markers property external
                if (typeof source.external !== 'undefined') {
                    external = source.external;
                }

                // Make all img urls https
                if (data[i].images[0]) {
                    data[i].images[0] = data[i].images[0].replace('http://', 'https://');
                }

                // Convert html entities to text in title
                data[i].title = this.htmlEntities2Text(data[i].title);
                
                if (data[i].geometry.type == 'Point') {
                    markerLat = parseFloat(data[i].geometry.coordinates[1]);
                    markerLng = parseFloat(data[i].geometry.coordinates[0]);
                } else if (data[i].geometry.type == 'LineString') {
                    markerLat = parseFloat(data[i].geometry.coordinates[0][1]);
                    markerLng = parseFloat(data[i].geometry.coordinates[0][0]);

                    // Convert and add route
                    waypoints = this.convertRetkipaikkaRoute2ECM(data[i].geometry.coordinates);

                    route = {
                        id: data[i].id,
                        title: data[i].title,
                        image: data[i].images[0],
                        url: data[i].link,
                        target: target,
                        polylineOptions: {
                            "strokeColor": "#0099ff",
                            "strokeOpacity": 1,
                            "strokeWeight": 5
                        },
                        waypoints: waypoints
                    };

                    if (data[i].route_difficulty) {
                        route.badges = [
                            {
                                id: badgeIds[data[i].route_difficulty],
                                name: data[i].route_difficulty[0].toUpperCase() + data[i].route_difficulty.substring(1)
                            }
                        ];
                        route.polylineOptions.strokeColor = routeColors[data[i].route_difficulty];
                    }
                    
                    extRoutesArr.push(route);
                } else if (data[i].geometry.type == 'MultiLineString') {
                    // Just for testing...
                    continue;
                    // TO DO
                }

                let marker = {
                    id: data[i].id,
                    name: source.id,
                    title: data[i].title,
                    image: data[i].images[0],
                    logo: null,
                    color: null,
                    latitude: markerLat,
                    longitude: markerLng,
                    url: data[i].link,
                    target: target,
                    icon: icon,
                    iconSize: source.iconSize,
                    external: external
                };

                // Make sure url is not an empty string
                if (marker.url == '') {
                    marker.url = null;
                }

                // No need for target if url is null
                if (! marker.url) {
                    marker.target = null;
                }

                extMarkersArr.push(marker);
            }

            if (extRoutesArr.length > 0)  {
                let payload = {
                    name: source.id,
                    routes: extRoutesArr
                };

                this.$store.commit('setExternalRoutes', payload);
            }

            return extMarkersArr;
        },
        /**
         * @param   {array}     data
         * @return  {array}
         */
        convertRetkipaikkaRoute2ECM(data) {
            
           let waypoints = [];

            if (Array.isArray(data)) {
                for (let i = 0; i < data.length; i++) {
                    waypoints.push({
                        lat: data[i][1],
                        lng: data[i][0]
                    });
                }
            }

            return waypoints;
        },
        /**
         * Converts html entities to text in a string.
         * @param   {string}    str
         */
        htmlEntities2Text(str) {
            let textArea = document.createElement('textarea');
            textArea.innerHTML = str;
            let newStr = textArea.value;
            textArea = null;
            return newStr;
        },
        /**
         * @param   {string}    url
         * @param   {object}    bounds
         * @returns {string}
         */
        replaceUrlParams(url, bounds) {
            url = url.replace('{swLat}', bounds.getSouthWest().lat);
            url = url.replace('{swLng}', bounds.getSouthWest().lng);
            url = url.replace('{neLat}', bounds.getNorthEast().lat);
            url = url.replace('{neLng}', bounds.getNorthEast().lng);
            url = url.replace('{lan}', this.lan);

            // Strip lan=fi if we're getting Retkipaikka articles from explori.es
            // This is kinda hack because of Explories API limitations
            if (url.search('explori.es') !== -1 && url.search('publicationId=8') !== -1) {
                // Just in case lan is the first param
                url = url.replace('?lan=fi&', '?');
                // Strip lan
                url = url.replace('&lan=fi', '');
            }

            return url;
        },
        inlineStyles() {
            let styles = {
                '--km-theme-color': this.themeColor,
                '--km-theme-text-color': this.themeTextColor,
                '--km-theme-hover-color': this.themeHoverColor
            };

            // Set width and height if boxed is set
            if (this.config && this.config.boxed) {
                // Set width if defined
                if (this.config.boxedWidth) {
                    if (! isNaN(this.config.boxedWidth)) {
                        // Its a numeric value, default to px
                        this.config.boxedWidth += 'px';
                    }
                    styles.width = this.config.boxedWidth;
                }
                // Set width if defined
                if (this.config.boxedHeight) {
                    if (! isNaN(this.config.boxedHeight)) {
                        // Its a numeric value, default to px
                        this.config.boxedHeight += 'px';
                    }
                    styles.height = this.config.boxedHeight;
                }
            }
            
            return styles;
        },
        isFunction(functionToCheck) {
            return functionToCheck && {}.toString.call(functionToCheck) === '[object Function]';
        }
    }
}

</script>

<style scoped>

</style>
